// Reassign an RFQ to a different customer (and optionally one of their contacts).
//
// Why this exists: email/chatbot intake can land an RFQ on a placeholder
// "(unknown)" customer when it can't match the sender to a known company. Until
// now the RFQ was stuck there forever with no way to correct it.
//
// Re-attribution is real, not cosmetic: quotes and invoices resolve the buyer
// through the RFQ→customer join at render time, so moving the RFQ moves every
// document with it — plus the RFQ's history and revenue onto the new customer's
// scorecard. That's the point, but it's why we confirm when quotes already went
// out.

import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, AlertTriangle, Building2, Check } from 'lucide-react';
import {
  api,
  type ListCustomersResponse,
  type CustomerListItem,
  type CustomerScorecardResponse,
} from '@/lib/api';
import { useModalDismiss } from '@/lib/use-modal-dismiss';

interface Props {
  rfqPublicId: string;
  /** Current customer, if any — shown so the broker sees what they're moving from. */
  currentCustomerName: string | null;
  currentCustomerPublicId: string | null;
  /** Documents already issued on this RFQ (sent/accepted quotes, proformas,
   *  invoices). Each one froze the OLD buyer into its html_snapshot, so they do
   *  NOT follow the RFQ to the new customer — the broker has to reissue them. */
  issuedDocCount: number;
  onClose: () => void;
}

export function RfqReassignCustomerModal({
  rfqPublicId,
  currentCustomerName,
  currentCustomerPublicId,
  issuedDocCount,
  onClose,
}: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const dismiss = useModalDismiss(onClose);

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [picked, setPicked] = useState<CustomerListItem | null>(null);
  const [contactId, setContactId] = useState<string | null>(null);

  // Debounce so typing doesn't fire a search per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const search = useQuery({
    queryKey: ['customers', { q: debounced }],
    queryFn: () =>
      api<ListCustomersResponse>(
        `/api/customers${debounced ? `?q=${encodeURIComponent(debounced)}` : ''}`,
        { tokenGetter: () => getToken() },
      ),
    staleTime: 10_000,
  });

  // Contacts live on the customer scorecard — fetch once a customer is picked so
  // the broker can also attach the right person in the same step.
  const scorecard = useQuery({
    queryKey: ['customer', picked?.id],
    queryFn: () =>
      api<CustomerScorecardResponse>(`/api/customers/${picked!.id}`, {
        tokenGetter: () => getToken(),
      }),
    enabled: !!picked,
    staleTime: 10_000,
  });

  const reassign = useMutation({
    mutationFn: () =>
      api(`/api/rfqs/${rfqPublicId}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify({
          customerId: picked!.id,
          // null explicitly clears any stale contact from the old customer.
          contactId: contactId,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rfq', rfqPublicId] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
      qc.invalidateQueries({ queryKey: ['customers'] });
      if (currentCustomerPublicId) qc.invalidateQueries({ queryKey: ['customer', currentCustomerPublicId] });
      if (picked) qc.invalidateQueries({ queryKey: ['customer', picked.id] });
      // Per-customer aggregates moved too — without these the dashboard and
      // reports keep showing the RFQ under the old customer until they go stale.
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['reports'] });
      onClose();
    },
  });

  const results = search.data?.customers ?? [];
  const contactsForPicked = scorecard.data?.contacts ?? [];

  // One contact = no choice to make. Preselect it, or the broker reassigns the
  // customer correctly and still leaves the contact empty.
  useEffect(() => {
    if (contactsForPicked.length === 1 && contactId === null) {
      setContactId(contactsForPicked[0]!.id);
    }
  }, [contactsForPicked, contactId]);
  const errMsg = reassign.isError
    ? (reassign.error as { message?: string })?.message ?? 'Reassign failed'
    : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto"
      {...dismiss}
    >
      <div className="bg-surface border border-border rounded-lg shadow-lg w-full max-w-lg mt-16">
        <div className="px-4 py-3 border-b border-border flex items-start justify-between gap-2">
          <div>
            <div className="text-sm font-semibold">Reassign customer</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Currently:{' '}
              {currentCustomerName ? (
                <span className="font-medium text-foreground">{currentCustomerName}</span>
              ) : (
                <span className="italic">Unknown</span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-sm"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="px-4 py-3 space-y-3">
          {issuedDocCount > 0 && (
            <div className="flex gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded p-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden />
              <div>
                <span className="font-medium">
                  {issuedDocCount} document{issuedDocCount === 1 ? '' : 's'} already issued on this
                  RFQ will keep the old customer's name.
                </span>{' '}
                Quotes and invoices freeze the buyer into the document when they're generated, so
                reassigning does <span className="font-medium">not</span> correct anything already
                sent — reissue those separately. The RFQ's history and revenue do move to the new
                customer.
              </div>
            </div>
          )}

          <label className="block">
            <span className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
              Find customer
            </span>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" aria-hidden />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPicked(null);
                  setContactId(null);
                }}
                placeholder="Name, email domain, contact…"
                className="w-full pl-7 pr-2 py-1.5 border border-border rounded text-sm"
              />
            </div>
          </label>

          <div className="border border-border rounded max-h-56 overflow-auto divide-y divide-border">
            {search.isLoading && (
              <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>
            )}
            {!search.isLoading && results.length === 0 && (
              <div className="px-3 py-2 text-xs text-muted-foreground">No customers found.</div>
            )}
            {results.map((c) => {
              const isCurrent = c.id === currentCustomerPublicId;
              const isPicked = picked?.id === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  disabled={isCurrent}
                  onClick={() => {
                    setPicked(c);
                    setContactId(null);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed ${
                    isPicked ? 'bg-muted' : ''
                  }`}
                >
                  <Building2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{c.displayName ?? c.legalName}</span>
                    <span className="block text-xs text-muted-foreground truncate">
                      {[c.emailDomain, c.countryCode, `${c.lifetimeRfqs} RFQs`]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  {isCurrent && <span className="text-xs text-muted-foreground">current</span>}
                  {isPicked && <Check className="w-4 h-4 text-primary flex-shrink-0" aria-hidden />}
                </button>
              );
            })}
          </div>

          {picked && (
            <label className="block">
              <span className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
                Contact at {picked.displayName ?? picked.legalName}
              </span>
              <select
                value={contactId ?? ''}
                onChange={(e) => setContactId(e.target.value || null)}
                className="w-full px-2 py-1.5 border border-border rounded text-sm"
                disabled={scorecard.isLoading}
              >
                <option value="">
                  {scorecard.isLoading ? 'Loading contacts…' : 'No contact'}
                </option>
                {contactsForPicked.map((ct) => (
                  <option key={ct.id} value={ct.id}>
                    {ct.fullName ? `${ct.fullName} · ${ct.email}` : ct.email}
                  </option>
                ))}
              </select>
              {!scorecard.isLoading && contactsForPicked.length === 0 && (
                <span className="block text-[11px] text-muted-foreground mt-0.5">
                  This customer has no contacts on file yet.
                </span>
              )}
            </label>
          )}

          {errMsg && <div className="text-xs text-destructive">{errMsg}</div>}
        </div>

        <div className="px-4 py-3 border-t border-border flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-2.5 py-1 text-xs border border-border rounded hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => reassign.mutate()}
            disabled={!picked || reassign.isPending}
            className="px-3 py-1 text-xs font-medium bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-50"
          >
            {reassign.isPending ? 'Reassigning…' : 'Reassign'}
          </button>
        </div>
      </div>
    </div>
  );
}
